"use client"

import { FragmentType, graphql, useFragment } from "@/gql"
import { getFragmentData } from "@/gql-clients/experimental/getFragmentData"
import {
  AllJobApplicationsDocument,
  JobInvitationsDocument,
  JobOwnerHeaderQueryDocument,
  JobSuggestedCreativesDocument,
  UnreviewedJobApplicationsDocument,
} from "@/gql/graphql"
import type { NovaSession } from "@/types"
import { ApolloClient, useApolloClient } from "@apollo/client"
import Link from "next/link"
import { useCallback } from "react"
import { Action, determineJobPostingAction } from "../../utilts"
import { MyJobActions } from "./MyJobActions"
import { MyJobActionsMobile } from "./MyJobActionsMobile"
import { MyJobsJobPostMetadata } from "./MyJobsJobPostMetadata"
import { MyJobsJobPostMetadataMobile } from "./MyJobsJobPostMetadataMobile"
import { ampli } from "@/ampli"
import { decodeGraphqlNodeId } from "@/tracking"

const MyJobPostsItemFragment = graphql(`
  fragment MyJobPostsItem on JobPosting {
    id
    __typename
    ...MyJobActionButtons
    ...MyJobsJobPostMetadata
    ...MyJobsJobPostStatusDeterminator
    position {
      id
    }
    unreviewedApplicationCount
  }
`)

function getActionString(action: Action, unreviewedApplicationCount: number) {
  switch (action) {
    case Action.SEND_INVITE:
      return "?tab=invites"
    case Action.REVIEW_APPLICATIONS:
      if (unreviewedApplicationCount > 0) {
        return "?tab=applications&filter=unreviewed"
      }
    default:
      return "?tab=applications"
  }
}

function prefetch(
  action: Action,
  client: ApolloClient<object>,
  jobPostingFragment: FragmentType<typeof MyJobPostsItemFragment>,
  serverSession: NovaSession
) {
  const jobPosting = getFragmentData(MyJobPostsItemFragment, jobPostingFragment)
  client.query({ query: JobOwnerHeaderQueryDocument, variables: { id: jobPosting.id }, context: { version: 2 } })
  if (action === Action.REVIEW_APPLICATIONS && jobPosting.unreviewedApplicationCount > 0) {
    client.query({
      query: UnreviewedJobApplicationsDocument,
      variables: {
        id: jobPosting.id,
        first: 4,
        isCreativeViewer: serverSession.account === "creative",
      },
      context: { version: 2 },
    })
  } else if (action === Action.SEND_INVITE) {
    client.query({
      query: JobInvitationsDocument,
      variables: { id: jobPosting.id, first: 6, after: "", isCreativeViewer: serverSession.account === "creative" },
      context: { version: 2 },
    })
    client.query({
      query: JobSuggestedCreativesDocument,
      variables: { id: jobPosting.id, isCreativeViewer: serverSession.account === "creative" },
      context: { version: 2 },
    })
  } else {
    client.query({
      query: AllJobApplicationsDocument,
      variables: {
        id: jobPosting.id,
        first: 4,
        isCreativeViewer: serverSession.account === "creative",
      },
      context: { version: 2 },
    })
  }
}

const JobPostContent: React.FC<{
  eagerImage: boolean
  jobPosting: FragmentType<typeof MyJobPostsItemFragment>
  serverSession: NovaSession
}> = ({ eagerImage, serverSession, ...props }) => {
  const jobPosting = useFragment(MyJobPostsItemFragment, props.jobPosting)

  return (
    <>
      <MyJobsJobPostMetadata eagerImage={eagerImage} jobPosting={jobPosting} />
      <MyJobsJobPostMetadataMobile eagerImage={eagerImage} jobPosting={jobPosting} />
      <MyJobActions jobPosting={jobPosting} serverSession={serverSession} />
      <MyJobActionsMobile jobPosting={jobPosting} serverSession={serverSession} />
    </>
  )
}

interface Props {
  jobPosting: FragmentType<typeof MyJobPostsItemFragment>
  eagerImage: boolean
  serverSession: NovaSession
  onClick?: () => void
}

export const MyJobPostsItem: React.FC<Props> = ({ serverSession, eagerImage, onClick, ...props }) => {
  const jobPosting = useFragment(MyJobPostsItemFragment, props.jobPosting)
  const client = useApolloClient()
  const action = determineJobPostingAction(jobPosting)
  const actionString = getActionString(action, jobPosting.unreviewedApplicationCount)

  const handlePrefetch = useCallback(() => {
    prefetch(action, client, props.jobPosting, serverSession)
  }, [action, client, props.jobPosting, serverSession])

  return (
    <>
      {/* Mobile Link without action string */}
      <Link
        onMouseEnter={handlePrefetch}
        onClick={onClick}
        href={`/hire/${jobPosting.id}`}
        className="-mt-[1px] block w-full flex-col gap-x-4 rounded-xl p-5 shadow-[0_4px_24px_0px_rgba(0,0,0,0.1)] hover:bg-bg-2 sm:p-8 md:hidden">
        <JobPostContent eagerImage={eagerImage} serverSession={serverSession} {...props} />
      </Link>

      {/* Desktop Link with action string */}
      <Link
        onMouseEnter={handlePrefetch}
        onClick={onClick}
        href={`/hire/${jobPosting.id}${actionString}`}
        className="-mt-[1px] hidden w-full flex-row items-center justify-between gap-x-4 rounded-xl p-5 shadow-[0_4px_24px_0px_rgba(0,0,0,0.1)] hover:bg-bg-2 sm:p-8 sm:shadow-none md:flex">
        <JobPostContent eagerImage={eagerImage} serverSession={serverSession} {...props} />
      </Link>
    </>
  )
}
